import React, { useEffect, useState } from 'react';
import { Calendar, Loader2, Plus, Save } from 'lucide-react';
import { toast } from 'sonner';
import { createFieldTask, getStaffMembers } from '../api';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';

const emptyTask = { description: '', due_date: '', assigned_to: [] };

export const ManualFollowUpTaskDialog = ({ buyerVisit, onCreated }) => {
  const [open, setOpen] = useState(false);
  const [task, setTask] = useState(emptyTask);
  const [staff, setStaff] = useState([]);
  const [loadingStaff, setLoadingStaff] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || staff.length) return;
    setLoadingStaff(true);
    getStaffMembers(true)
      .then((response) => setStaff(response.data || []))
      .catch(() => toast.error('Failed to load staff members'))
      .finally(() => setLoadingStaff(false));
  }, [open, staff.length]);

  const handleOpenChange = (nextOpen) => {
    if (saving) return;
    setOpen(nextOpen);
    if (!nextOpen) setTask(emptyTask);
  };

  const toggleAssignee = (staffId) => {
    setTask((current) => ({
      ...current,
      assigned_to: current.assigned_to.includes(staffId)
        ? current.assigned_to.filter((id) => id !== staffId)
        : [...current.assigned_to, staffId],
    }));
  };

  const saveTask = async () => {
    if (!task.description.trim()) {
      toast.error('Task description is required');
      return;
    }
    if (!task.due_date) {
      toast.error('Due date is required');
      return;
    }
    setSaving(true);
    try {
      const response = await createFieldTask({
        visit_id: buyerVisit.id,
        buyer_id: buyerVisit.buyer_id,
        description: task.description.trim(),
        due_date: task.due_date,
        assigned_to: task.assigned_to,
      });
      onCreated(response.data);
      toast.success('Follow-up task added');
      setOpen(false);
      setTask(emptyTask);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add follow-up task');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="min-h-[44px] sm:min-h-8 px-2 text-xs"
        onClick={() => setOpen(true)}
        data-testid={`add-manual-task-${buyerVisit.id}`}
      >
        <Plus className="mr-1 h-3.5 w-3.5" /> Add Task
      </Button>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="max-w-lg bg-white" data-testid={`manual-task-dialog-${buyerVisit.id}`}>
          <DialogHeader>
            <DialogTitle>Add follow-up task — {buyerVisit.buyer_name}</DialogTitle>
            <DialogDescription>The task will be added as Pending to this buyer visit.</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label htmlFor={`manual-task-description-${buyerVisit.id}`}>Description</Label>
              <Textarea
                id={`manual-task-description-${buyerVisit.id}`}
                value={task.description}
                onChange={(event) => setTask((current) => ({ ...current, description: event.target.value }))}
                rows={3}
                placeholder="What needs to be followed up?"
                data-testid={`manual-task-description-${buyerVisit.id}`}
              />
            </div>
            <div>
              <Label htmlFor={`manual-task-due-${buyerVisit.id}`}>Due date</Label>
              <div className="relative">
                <Calendar className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                <Input
                  id={`manual-task-due-${buyerVisit.id}`}
                  type="date"
                  value={task.due_date}
                  onChange={(event) => setTask((current) => ({ ...current, due_date: event.target.value }))}
                  className="pl-9"
                  data-testid={`manual-task-due-${buyerVisit.id}`}
                />
              </div>
            </div>
            <div>
              <Label>Assignee(s) <span className="font-normal text-gray-500">optional</span></Label>
              {loadingStaff ? (
                <div className="flex items-center gap-2 py-2 text-sm text-gray-500" data-testid={`manual-task-staff-loading-${buyerVisit.id}`}>
                  <Loader2 className="h-4 w-4 animate-spin" /> Loading staff…
                </div>
              ) : staff.length === 0 ? (
                <p className="py-2 text-sm text-gray-500">No active staff members.</p>
              ) : (
                <div className="mt-1 grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-40 overflow-y-auto" data-testid={`manual-task-staff-${buyerVisit.id}`}>
                  {staff.map((member) => (
                    <label key={member.id} className="flex min-h-[44px] items-center gap-2 rounded border border-gray-200 px-3 py-2 text-sm cursor-pointer hover:bg-gray-50">
                      <Checkbox
                        checked={task.assigned_to.includes(member.id)}
                        onCheckedChange={() => toggleAssignee(member.id)}
                        data-testid={`manual-task-assignee-${buyerVisit.id}-${member.id}`}
                      />
                      <span className="truncate">{member.name}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => handleOpenChange(false)} disabled={saving} data-testid={`cancel-manual-task-${buyerVisit.id}`}>Cancel</Button>
            <Button type="button" onClick={saveTask} disabled={saving} className="bg-maroon-700 hover:bg-maroon-800" data-testid={`save-manual-task-${buyerVisit.id}`}>
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
              Add Follow-up Task
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};